import React, { Component } from 'react';
import { compose } from 'redux';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as util from '../../utils/urlParams';
import { DiagnosticsActions, NetworkActions } from '../../store';
import { Error404Page } from '../Error404Page';
import { ConnectedMainDisplayBoxWithRouter } from '../MainDisplayBox';
import { ConnectedDiagnosticsLegendWithRouter } from '../DiagnosticsLegend';
import { SelectedBuilding, NavigationParamsType } from '../../store/building/types';
import { DiagnosticsData, DataPointListObj, GraphDateRange } from '../../store/diagnostics/types';
import '../MainDisplayBox/MainDisplayBox.css';

interface SelectOption {
  value: string;
  label: string;
  startDatetime?: string;
  endDatetime?: string;
}

interface State {
  isEditing: boolean;
}

export interface Props {
  clearErrors(): void;
  clearLastRequest(): void;
  diagnosticsData: DiagnosticsData;
  dataPointsList: DataPointListObj[];
  graphDateRange: GraphDateRange;
  invalidSelectedBuildingError: string;
  isLoading: boolean;
  loadDiagnosticData(
    api_location: string,
    data_api_location: string,
    requestType: string,
    registerNumbers: string[],
  ): void;
  pollDiagnostics(
    slug: string,
    subcategory: string,
    timestamp: string,
    startDatetime: string,
    endDatetime: string,
  ): boolean;
  selectedBuilding: SelectedBuilding;
  navigationParams: NavigationParamsType;
  updateDiagnosticsGraphDateRange(filter: SelectOption, registerNumbers: string[]): void;
}

let pollDiagnosticsTimer: any;

declare type ComposedProps = Props & RouteComponentProps;

export class Diagnostics extends Component<ComposedProps, State> {
  state = {
    isEditing: false,
  };

  componentDidMount() {
    const {
      location,
      navigationParams: {
        selectedSubCategory: { api_location, data_api_location, url, id },
      },
      updateDiagnosticsGraphDateRange,
      loadDiagnosticData,
    } = this.props;

    let { search } = location;
    let filter = util.getDiagnosticsFilterFromUrlParams(search) as SelectOption;
    let dataPoints = util.getDataPointsFromUrlParams(search) as string[];

    if (!filter) {
      const newSearch = this.getDateSearch(dataPoints);
      this.props.history.replace(`${url}?${newSearch}`);
    }

    if (filter) {
      updateDiagnosticsGraphDateRange(filter, dataPoints);

      if (filter.value === 'custom') {
        if (moment(new Date()).utc().diff(filter.endDatetime, 'hours') <= 24) {
          pollDiagnosticsTimer = window.setInterval(this.checkNewData, 60000, dataPoints);
        }
      } else if (filter.value === '24Hours' || filter.value === '48Hours') {
        pollDiagnosticsTimer = window.setInterval(this.checkNewData, 60000, dataPoints);
      }
    }

    return loadDiagnosticData(api_location, data_api_location, 'GET', dataPoints);
  }

  async componentDidUpdate(prevProps: ComposedProps) {
    const {
      location: { search: prevSearch },
      navigationParams: { selectedSubCategory: preSelectedSubCategory },
    } = prevProps;

    const {
      location: { search: newSearch },
      navigationParams: { selectedSubCategory: newSelectedSubCategory },
      updateDiagnosticsGraphDateRange,
      loadDiagnosticData,
      dataPointsList,
    } = this.props;
    const newRegisterNumbers = util.getDataPointsFromUrlParams(newSearch) as string[];

    const lineColorObjStr = window.sessionStorage.getItem('lineColorObj');
    const availableColorsStr = window.sessionStorage.getItem('availableColors');

    // This block is used for when there are custom registers in the url and the user
    // manually removes some and navigates to the new url. Also to clean the url and remove
    // non-active data points
    if (dataPointsList && lineColorObjStr && availableColorsStr) {
      let filteredDataPoints: string[] = [];
      newRegisterNumbers.map((dataPoint) => {
        dataPointsList.map((dataPointObj) => {
          if (dataPointObj.tag === dataPoint) {
            filteredDataPoints.push(dataPoint);
            return;
          }
        });
      });

      const lineColorObj = JSON.parse(lineColorObjStr);
      const availableColors = JSON.parse(availableColorsStr);
      const removedDataPoints = newRegisterNumbers.filter((el) => !filteredDataPoints.includes(el));
      removedDataPoints.forEach((el) => {
        if (lineColorObj[el]) {
          availableColors.push(lineColorObj[el]);
        }
        delete lineColorObj[el];
      });

      window.sessionStorage.setItem('lineColorObj', JSON.stringify(lineColorObj));
      window.sessionStorage.setItem('availableColors', JSON.stringify(availableColors));
    }

    if (preSelectedSubCategory.id !== newSelectedSubCategory.id) {
      window.sessionStorage.removeItem('lineColorObj');
      window.sessionStorage.removeItem('availableColors');

      return loadDiagnosticData(
        newSelectedSubCategory.api_location,
        newSelectedSubCategory.data_api_location,
        'GET',
        newRegisterNumbers,
      );
    }

    if (prevSearch !== newSearch) {
      // Fetch new data if date range is updated
      const prevFilter = util.getDiagnosticsFilterFromUrlParams(prevSearch);
      const newFilter = util.getDiagnosticsFilterFromUrlParams(newSearch);

      if (newFilter && JSON.stringify(prevFilter) !== JSON.stringify(newFilter)) {
        await updateDiagnosticsGraphDateRange(newFilter, newRegisterNumbers);
      }
      return loadDiagnosticData(
        newSelectedSubCategory.api_location,
        newSelectedSubCategory.data_api_location,
        'GET',
        newRegisterNumbers,
      );
    }
  }

  componentWillUnmount() {
    window.clearInterval(pollDiagnosticsTimer);
    this.props.clearErrors();
  }

  getDateSearch(registerNumbers: string[]) {
    const { graphDateRange } = this.props;

    const diagnosticsDateFromSession = window.sessionStorage.getItem('diagnosticsGraphDateRange');
    const diagnosticsGraphDateRange = diagnosticsDateFromSession
      ? JSON.parse(diagnosticsDateFromSession)
      : graphDateRange;

    const search = util.addSearchParams(diagnosticsGraphDateRange, registerNumbers);

    return search;
  }

  setEditingState = (isEditing: boolean) => {
    this.setState((state) => ({
      ...state,
      isEditing,
    }));
  };

  checkNewData = async (registerNumbers: string[]) => {
    const {
      selectedBuilding: { identifier },
      navigationParams: {
        selectedSubCategory: { name, api_location, data_api_location },
      },
      diagnosticsData: { data_points },
      loadDiagnosticData,
    } = this.props;
    const { isEditing } = this.state;

    const currentTimeUtc = moment.utc(new Date()).subtract(10, 'seconds').format();

    // only check for new data if there is current data
    if (data_points) {
      const firstRegisterData = data_points[0].graph_data;
      const dataLength = firstRegisterData.length;
      const firstDataTime = firstRegisterData[0].x;
      const lastDataTime = firstRegisterData[dataLength - 1].x;
      const firstDataTimeUtc = moment.utc(firstDataTime).format();
      const lastDataTimeUtc = moment.utc(lastDataTime).format();

      const shouldUpdateGraph = await this.props.pollDiagnostics(
        identifier,
        name.toLowerCase(),
        currentTimeUtc,
        firstDataTimeUtc,
        lastDataTimeUtc,
      );

      // if result says to retrieve new data then resend loadDiagnosticsData
      if (shouldUpdateGraph && !isEditing) {
        loadDiagnosticData(api_location, data_api_location, 'GET', registerNumbers);
      }
    }
  };

  render() {
    const { isLoading, invalidSelectedBuildingError } = this.props;
    if (invalidSelectedBuildingError) {
      return <Error404Page />;
    }

    if (isLoading) {
      return (
        <React.Fragment>
          <ConnectedMainDisplayBoxWithRouter isLoading={true} />
          <ConnectedDiagnosticsLegendWithRouter setEditingState={this.setEditingState} />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <ConnectedMainDisplayBoxWithRouter isLoading={false} />
        <ConnectedDiagnosticsLegendWithRouter setEditingState={this.setEditingState} />
      </React.Fragment>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    isLoading: state.diagnosticsReducer.isLoading,
    invalidSelectedBuildingError: state.buildingReducer.error,
    selectedBuilding: state.buildingReducer.selectedBuilding,
    navigationParams: state.buildingReducer.navigationParams,
    graphDateRange: state.diagnosticsReducer.graphDateRange,
    diagnosticsData: state.diagnosticsReducer.diagnosticsData,
    dataPointsList: state.diagnosticsReducer.dataPointsList,
  };
}

const mapDispatchToProps = {
  loadDiagnosticData: DiagnosticsActions.loadDiagnosticData,
  updateDiagnosticsGraphDateRange: DiagnosticsActions.updateDiagnosticsGraphDateRange,
  clearErrors: DiagnosticsActions.clearDiagnosticsError,
  clearLastRequest: NetworkActions.clearLastRequest,
  pollDiagnostics: DiagnosticsActions.pollDiagnostics,
};

export const ConnectedDiagnosticsWithRouter = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(Diagnostics);
